import type * as hast from 'hast';

import { TextAlignment } from '@gitbook/api-client';
import {
    BLOCK_DIVIDER,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_PARAGRAPH,
    type BlockParagraphData,
    MARK_BOLD,
} from '@gitbook/doc-core';
import { Block, Mark } from '@gitbook/slate';
import { normalizeURLAnchor } from '@gitbook/util-url';

import { processImagesWrapper } from './transform-images-maths';
import type { HastToGastTransformFn, HastToGastTransformMap } from './types';
import { getEnumAttribute, isWhitespace } from './utils';

export const transformBlockLeafs: HastToGastTransformMap = {
    /*
     * Paragraph
     */
    p: async (node: hast.Element, eat, context) => {
        // Not an actual <p> but only a <hr>
        const containsOnlyHR =
            node.children.length > 0 &&
            node.children.every(
                (node) => (node.type === 'element' && node.tagName === 'hr') || isWhitespace(node)
            );
        if (containsOnlyHR) {
            return eat('blocks', node.children, context);
        }

        // Parse as a wrapper around multiple images
        const imageNodes = await processImagesWrapper(node, eat, context);
        if (imageNodes) {
            return imageNodes;
        }

        // Parse as a normal paragraph
        const nodes = await eat('inlines', node.children, context);
        if (nodes.length === 0) {
            return [];
        }
        return [
            Block.create({
                type: BLOCK_PARAGRAPH,
                nodes,
                data: getTextAlign(node.properties),
            }),
        ];
    },
    hr: async () => {
        return [
            Block.create({
                type: BLOCK_DIVIDER,
                isVoid: true,
            }),
        ];
    },

    /*
     * Headings
     */
    h1: createHeadingTransform(1),
    h2: createHeadingTransform(2),
    h3: createHeadingTransform(3),
    h4: createHeadingTransform(4),
    h5: createHeadingTransform(5),
    h6: createHeadingTransform(6),
};

const headingTypes = [BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3];

/**
 * Transform an heading.
 * It takes of extracting the page title if requested;
 * and also to fallback non supported headings to a bold paragraph.
 */
function createHeadingTransform(
    /** Level of the heading, starts at 1 for h1 */
    level: number
): HastToGastTransformFn {
    return async (node: hast.Element, eat, context, options, parent, index) => {
        const isTopLevel = parent.type === 'root';

        const type =
            headingTypes[level - 1 - (isTopLevel ? context.titleHeadingLevel || 0 : 0)] ||
            BLOCK_PARAGRAPH;

        const isFallback = type === BLOCK_PARAGRAPH;

        const block = Block.create({
            type,
            nodes: await eat('inlines', node.children, {
                ...context,
                ...(isFallback ? { marks: [Mark.create(MARK_BOLD)] } : {}),
            }),
            data: {
                ...(typeof node.properties.id === 'string' && !isFallback
                    ? { id: normalizeURLAnchor(node.properties.id) }
                    : {}),
                ...getTextAlign(node.properties),
            },
        });

        if (
            isTopLevel &&
            index === 0 &&
            options.mode === 'page' &&
            // No other node has the same level
            // @ts-expect-error
            !parent.children?.slice(1).find((el) => el.tagName === node.tagName) &&
            // Not an empty heading
            !block.isEmpty
        ) {
            // If the doc is starting by a heading, we'll extract it as page title later on
            // but we want to decrease the next headlings levels now
            context.attributes.title = block.text;

            context.titleHeadingLevel = level;

            return [];
        }

        return [block];
    };
}

function getTextAlign(properties: hast.Properties): BlockParagraphData {
    const align = getEnumAttribute(properties, 'align', ['left', 'center', 'right'], 'left');

    switch (align) {
        case 'left':
            return {};
        case 'right':
            return { align: TextAlignment.End };
        case 'center':
            return { align: TextAlignment.Center };
    }
}
